import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ControlFilters {
  status?: string;
  domain?: string;
}

interface CreateControlData {
  control_id: string;
  title: string;
  description?: string;
  domain: string;
  standard_key: string;
  status?: string;
  gap_statement?: string;
  remediation?: string;
  due_date?: string;
  owner_id?: string;
}

interface UpdateControlData {
  control_id?: string;
  title?: string;
  description?: string;
  domain?: string;
  standard_key?: string;
  status?: string;
  gap_statement?: string;
  remediation?: string;
  due_date?: string;
  owner_id?: string;
}

export const useControls = (orgId?: string, filters?: ControlFilters) => {
  const queryClient = useQueryClient();

  const controlsQuery = useQuery({
    queryKey: ['controls', orgId, filters?.status, filters?.domain],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('controls')
        .select('*, owner:profiles!fk_controls_owner(name, email)')
        .eq('org_id', orgId);

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      if (filters?.domain && filters.domain !== 'all') {
        query = query.eq('domain', filters.domain);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!orgId,
  });

  const createControlMutation = useMutation({
    mutationFn: async (data: CreateControlData & { org_id: string }) => {
      const { error } = await supabase.from('controls').insert([{
        control_id: data.control_id,
        title: data.title,
        description: data.description,
        domain: data.domain,
        status: data.status || 'not_implemented',
        standard_key: data.standard_key,
        gap_statement: data.gap_statement,
        remediation: data.remediation,
        due_date: data.due_date,
        owner_id: data.owner_id || null,
        org_id: data.org_id,
      }]);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      toast.success('Control created successfully');
    },
    onError: (error) => {
      console.error('Error creating control:', error);
      toast.error('Failed to create control');
    },
  });

  const updateControlMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateControlData }) => {
      const updateData = {
        ...data,
        owner_id: data.owner_id === undefined ? undefined : (data.owner_id || null),
      };
      const { error } = await supabase
        .from('controls')
        .update(updateData)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      toast.success('Control updated successfully');
    },
    onError: (error) => {
      console.error('Error updating control:', error);
      toast.error('Failed to update control');
    },
  });

  const deleteControlMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the control
      const { error } = await supabase.from('controls').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['controls'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Control deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting control:', error);
      toast.error('Failed to delete control');
    },
  });

  return {
    controls: controlsQuery.data,
    isLoading: controlsQuery.isLoading,
    error: controlsQuery.error,
    createControl: createControlMutation.mutate,
    updateControl: updateControlMutation.mutate,
    deleteControl: deleteControlMutation.mutate,
    isCreating: createControlMutation.isPending,
    isUpdating: updateControlMutation.isPending,
    isDeleting: deleteControlMutation.isPending,
  };
};
